import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewGuest } from '../../hooks/use-get-default-values';
import {
  TicketOrderFlightPurchaseGuestFormType,
  TicketOrderFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetScheduleGuestTableList(): IColumn<TicketOrderFlightPurchaseGuestFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();

  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 200,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    {
      header: t('common:ticket'),
      accessorKey: 'ticket',
      minSize: 100,
      dataType: ColumnDataType.text,
    },
    ...deleteColumn(editable),
  ];
}

export default function ScheduleGuestTable(props: Props) {
  const { editable } = useFormState();
  const name =
    `flightPurchases.${props.parentIndex}.flightPurchaseGuests` as const;
  const addOnsName =
    `flightPurchases.${props.parentIndex}.flightPurchaseAddOns` as const;

  const { clearErrors, setValue, control, getValues, trigger } =
    useFormContext<TicketOrderFormType>();

  const { update, append, remove } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetScheduleGuestTableList();

  const dispatch = React.useCallback(
    (action) => {
      const guests = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const guest = guests[currentRowIndex];
      const type = action.type as ActionType;
      const columnId =
        action.columnId as keyof TicketOrderFlightPurchaseGuestFormType;
      const value = action.value;
      const extra = action.extra as Value;
      const otherVal: () => Partial<TicketOrderFlightPurchaseGuestFormType> =
        () => {
          if (columnId === 'guestId') {
            return { guestName: extra?.[0]?.label as string };
          }
          return {};
        };

      if (type === 'addRow') {
        append(addNewGuest());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        const addons = getValues(addOnsName).map((addon) => {
          if (addon.guestId === guest.guestId) {
            return {
              ...addon,
              guestId: '',
            };
          }
          return addon;
        });
        setValue(addOnsName, addons);
        trigger(addOnsName);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(name);
      }
    },
    [
      addOnsName,
      append,
      clearErrors,
      getValues,
      name,
      remove,
      setValue,
      trigger,
      update,
    ],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={getValues(name)}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
